<!-- eslint-disable max-len -->
<template lang="pug">
  div.root
    div(v-if="isRlgDataLoading") Loading...
    div(v-else)
      h4.pb-2 Return Label Generation
      p
        | Before configuring return label generation for partners using self fufilment,
        | ensure that the partner is eligible for RLG.
      v-switch(
        v-model="isRlgEnabled"
        label="Return Label Generation (RLG)"
        :inset="true"
        :disabled="!isRlgEligible || isRlgToggleDisabled || isZRSPureActive"
      )
      div.mt-5(v-if="hasRlgChanged")
        v-textarea(
          filled
          autofocus
          label="Add reason for deactivation"
          v-model="inputReasonForDeactivation"
        )
      div.mt-3(v-if="reasonForDeactivation")
        p
          | Reason for deactivation: {{reasonForDeactivation}}

      v-flex(d-flex style="justify-content: flex-end;")
        v-btn.mr-2(
          data-testid="cancel-rlg-cta"
          :text="hasRlgChanged"
          color="grey"
          @click="resetRlgInput"
          elevation="2"
          :disabled="!hasRlgChanged"
        ) Cancel
        v-btn(
          data-testid="save-rlg-cta"
          color="primary"
          @click="onRlgSaveClicked"
          :disabled="!hasRlgChanged || !inputReasonForDeactivation"
        ) Save

    v-dialog(v-model="showRlgChangesModal" width="550")
      v-card
        mpAlert.text-h6(type="warning") Deactivate label generation in {{countryName}} for Partner {{merchantName}}?

        v-card-text.pa-2
          p.p-4
            | Return labels will not be generated for customer returns in this country for this partner.

          v-card-actions
            v-spacer
            v-btn(text @click="showRlgChangesModal = false") Cancel

            v-btn(
              data-testid="confirm-rlg-changes-modal-save"
              color="primary"
              depressed
              @click="confirmDisableRlg"
              :loading="isRlgSaveLoading"
            ) Deactivate

</template>

<script lang="ts">
import Vue from 'vue';
import {
  RlgStatusT,
  ConfiguredCountryT,
} from 'Api/models/ReturnServiceProvider';
import Swal from 'sweetalert2';

export default Vue.extend({
  props: {
    merchantName: { type: String, required: true },
    countryName: { type: String, required: true },
    selectedRspConfiguredCountry: {
      type: Object as () => ConfiguredCountryT,
      required: false,
      default: null,
    },
    isRlgEligible: { type: Boolean, required: false, default: false },
    isZRSPureActive: { type: Boolean, required: false, default: false },
    isRlgDataLoading: { type: Boolean, required: false, default: false },
    onDisableRlg: { type: Function, required: true },
  },

  data() {
    return {
      isRlgSaveLoading: false,
      showRlgChangesModal: false,
      isRlgToggleDisabled: true,
      isRlgEnabled: false,
      initialRlgEnabled: null,
      reasonForDeactivation: null,
      inputReasonForDeactivation: '',
    };
  },

  computed: {
    hasRlgChanged() {
      if (this.initialRlgEnabled === null) return false;
      return this.isRlgEnabled !== this.initialRlgEnabled;
    },
  },

  watch: {
    selectedRspConfiguredCountry() {
      this.resetRlgState();
    },
  },

  mounted() {
    this.resetRlgState();
  },

  methods: {
    resetRlgState() {
      const rlg = this.selectedRspConfiguredCountry?.rlg;
      this.isRlgEnabled = rlg?.status === RlgStatusT.ACTIVE;
      this.initialRlgEnabled = this.isRlgEnabled;
      // No matching configured country is treated the same as a disabled,
      // non-configurable RLG: toggle stays off and can't be re-enabled here.
      this.isRlgToggleDisabled =
        !this.selectedRspConfiguredCountry || !this.isRlgEnabled;
      this.reasonForDeactivation =
        rlg?.status === RlgStatusT.DISABLED ? rlg.reason : null;
      this.inputReasonForDeactivation = '';
    },
    resetRlgInput() {
      this.isRlgEnabled = this.initialRlgEnabled;
      this.inputReasonForDeactivation = '';
    },
    onRlgSaveClicked() {
      this.showRlgChangesModal = true;
    },
    async confirmDisableRlg() {
      this.isRlgSaveLoading = true;

      try {
        await this.onDisableRlg(this.inputReasonForDeactivation);
        this.showRlgChangesModal = false;
        Swal.fire({
          html: 'Return Label Generation updated successfully!',
          timer: 2000,
          position: 'top-end',
          showConfirmButton: false,
          toast: true,
          icon: 'success',
        });
      } catch (e) {
        Swal.fire({
          html: 'Something went wrong while updating Return Label Generation. Please try again',
          timer: 2000,
          position: 'top-end',
          showConfirmButton: false,
          toast: true,
          icon: 'error',
        });
      }
      this.isRlgSaveLoading = false;
    },
  },
});
</script>
